import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/gateways";
import useSEO from "@/hooks/useSEO";
import MinisterCredential from "@/components/MinisterCredential";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2
      className="font-sans-body text-[10px] tracking-[0.36em] uppercase mb-3"
      style={{ color: "var(--olive)" }}
    >
      {title}
    </h2>
    <div
      className="font-sans-body text-[14px] leading-relaxed space-y-4"
      style={{ color: "var(--ink)" }}
    >
      {children}
    </div>
  </section>
);

const FAQ = ({ q, a }) => (
  <div className="pb-5 mb-5 border-b border-[rgba(176,133,94,0.25)] last:border-0 last:mb-0 last:pb-0">
    <p
      className="font-serif-display text-xl mb-2"
      style={{ color: "var(--ink)" }}
    >
      {q}
    </p>
    <p
      className="font-sans-body text-[14px] leading-relaxed"
      style={{ color: "var(--stone-deep)" }}
    >
      {a}
    </p>
  </div>
);

const Support = () => {
  useSEO({
    title: "Support — Selah & Stone Legacy Holdings",
    description:
      "Get support for Selah Slate, Selah Downloads, and the Selah and Stone family of brands. We reply within one business day.",
    path: "/support",
  });

  return (
    <div className="paper-grain relative min-h-screen" data-testid="support-page">
      <div className="relative z-10">
        {/* Top bar */}
        <div className="px-6 sm:px-10 pt-8">
          <div className="mx-auto max-w-3xl flex items-start justify-between">
            <Link to="/" className="leading-tight">
              <div
                className="font-sans-body text-[10px] sm:text-[11px] tracking-[0.32em] uppercase"
                style={{ color: "var(--ink)" }}
              >
                Selah &nbsp;&middot;&nbsp; Stone
              </div>
              <div
                className="font-sans-body text-[9px] sm:text-[10px] tracking-[0.42em] uppercase mt-1"
                style={{ color: "var(--olive)" }}
              >
                Legacy Holdings
              </div>
            </Link>
            <Link
              to="/"
              className="font-sans-body text-[11px] tracking-[0.22em] uppercase pt-1 underline hover:no-underline"
              style={{ color: "var(--stone-deep)" }}
              data-testid="support-back-link"
            >
              Back
            </Link>
          </div>
        </div>

        {/* Header */}
        <header className="px-6 sm:px-10 pt-16 pb-10">
          <div className="mx-auto max-w-3xl">
            <img
              src={LOGO_URL}
              alt="Selah and Stone Legacy Holdings"
              className="h-16 w-16 mb-6"
            />
            <p
              className="font-sans-body text-[10px] tracking-[0.42em] uppercase"
              style={{ color: "var(--olive)" }}
            >
              Support
            </p>
            <h1
              className="mt-3 font-serif-display text-5xl sm:text-6xl font-light leading-[1]"
              style={{ color: "var(--ink)" }}
            >
              We're here,
              <span
                className="block italic"
                style={{ color: "var(--olive)" }}
              >
                quietly.
              </span>
            </h1>
            <p
              className="mt-6 font-serif-display italic text-xl max-w-2xl"
              style={{ color: "var(--stone-deep)" }}
            >
              Every note is read personally. Reach out any time — we respond
              within one business day.
            </p>
          </div>
        </header>

        {/* Body */}
        <article className="px-6 sm:px-10 pb-20">
          <div className="mx-auto max-w-3xl">
            <Section title="Reach us">
              <p>
                The fastest way to reach a member of our team is by email:
              </p>
              <p className="pt-1">
                <a
                  href="mailto:admin@selahandstonelegacy.com"
                  className="font-serif-display text-2xl underline hover:no-underline"
                  style={{ color: "var(--olive)" }}
                  data-testid="support-email-link"
                >
                  admin@selahandstonelegacy.com
                </a>
              </p>
              <p className="pt-3 italic" style={{ color: "var(--stone-deep)" }}>
                We reply within one business day, Monday–Friday, US Eastern
                Time.
              </p>
            </Section>

            <Section title="What we can help with">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Questions or issues using <strong>Selah Slate</strong> at{" "}
                  <a
                    href="https://app.selahslate.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: "var(--olive)" }}
                  >
                    app.selahslate.com
                  </a>
                </li>
                <li>
                  Waitlist updates, unsubscribes, or corrections to your
                  submission
                </li>
                <li>
                  Scoping inquiries for{" "}
                  <strong>Selah and Stone Research</strong> and{" "}
                  <strong>Family Legacy Audits</strong> (coming soon)
                </li>
                <li>
                  Notary and loan-signing requests for{" "}
                  <strong>Omni Signing Services</strong>
                </li>
                <li>Press, partnership, and speaking inquiries</li>
                <li>
                  Anything else — if you're not sure where to begin, send a
                  note and we'll help you find the right gateway
                </li>
              </ul>
            </Section>

            <Section title="Frequently asked">
              <FAQ
                q="How do I use Selah Slate?"
                a="Selah Slate is a free web app you can open any time at app.selahslate.com. It's designed to assist you in erasing the financial burdens of the past — but meaningful restoration requires your active participation. Follow the in-app steps at your own pace."
              />
              <FAQ
                q="I signed up for a waitlist. When will I hear back?"
                a="Waitlists are per-gateway. You'll receive an email the moment that specific brand or product is ready to open its door — no bulk newsletters, no unrelated pitches. If you'd like to unsubscribe or change your email, just reply to any note from us or write to admin@selahandstonelegacy.com."
              />
              <FAQ
                q="How is a Family Legacy Audit conducted?"
                a="Family Legacy Audits are coming soon. Once we open, our audit is a deep-dive diagnostic conversation to identify breaches in your family's spiritual and structural legacy. After you pre-book, a member of our team will reach out to confirm timing and prepare you gently for what to expect."
              />
              <FAQ
                q="Does Selah and Stone Research provide investigative services?"
                a="No. Selah and Stone Research provides background screening and open-source forensic due diligence. We are not a licensed private investigative firm and do not engage in surveillance, undercover work, or other services reserved for licensed private investigators. All work is performed in accordance with applicable law, including the FCRA where relevant."
              />
              <FAQ
                q="Is Omni Signing Services able to give me legal or mortgage advice?"
                a="No. Notaries and signing agents may not select forms, draft documents, or explain the contents or legal implications of documents signed. Any planned mortgage origination services will be offered only through a properly licensed entity."
              />
              <FAQ
                q="How do I request my data or ask you to delete it?"
                a="Write to admin@selahandstonelegacy.com and tell us what you'd like. We'll respond within a reasonable period. See our Privacy Policy for the full details."
              />
            </Section>

            <Section title="Community and press">
              <p>
                For media inquiries, interviews, and speaking requests
                related to Selah and Stone Legacy Holdings or the
                forthcoming book <em>The Selah Shift</em>, please write to{" "}
                <a
                  href="mailto:admin@selahandstonelegacy.com"
                  className="underline"
                  style={{ color: "var(--olive)" }}
                >
                  admin@selahandstonelegacy.com
                </a>{" "}
                with &ldquo;Press&rdquo; in your subject line.
              </p>
            </Section>

            <Section title="Not a substitute">
              <p style={{ color: "var(--stone-deep)" }} className="italic">
                Nothing on this site or in a support reply constitutes legal,
                financial, tax, mortgage, medical, therapeutic, or
                professional advice. For urgent mental-health, legal, or
                medical needs, please contact a licensed professional in
                your area.
              </p>
            </Section>

            <div className="rule-ornament mt-16">
              <span
                className="font-serif-display italic text-sm"
                style={{ color: "var(--stone-deep)" }}
              >
                selah
              </span>
            </div>

            <MinisterCredential />
          </div>
        </article>
      </div>
    </div>
  );
};

export default Support;
